import {useEffect, useRef, useState} from 'react'
import {isServer} from '../utils/utils'
import useMediaQueries from '../contexts/media-queries'

/**
 * Recursively finds the value for a breakpoint by traversing up the breakpoint chain.
 * If the current breakpoint doesn't have a value, it looks at the previous breakpoint.
 *
 * @param {Object} breakpoints - The breakpoint values object (e.g., {base: 'Mobile', lg: 'Desktop'})
 * @param {Object} mq - The media query object containing breakpoint info and previousMq reference
 * @param {string} mq.breakpoint - The breakpoint name (e.g., 'base', 'sm', 'md', 'lg', 'xl', '2xl')
 * @param {Object|undefined} mq.query - The MediaQueryList object with .matches property
 * @returns {*|undefined} The value for the breakpoint, or undefined if no value is found in the chain
 */
const findBreakpointValue = (breakpoints, mediaQueries, activeBreakpointIndex) => {
    if (activeBreakpointIndex < 0 || activeBreakpointIndex >= mediaQueries.length) return undefined

    const mq = mediaQueries[activeBreakpointIndex]
    if (!mq) return undefined

    const {breakpoint} = mq
    const nextValue = breakpoints[breakpoint]
    if (nextValue !== undefined) {
        return nextValue
    } else {
        return findBreakpointValue(breakpoints, mediaQueries, activeBreakpointIndex - 1)
    }
}

/**
 * Finds the value for the currently active media query breakpoint.
 * Searches through all media queries to find the first one that matches,
 * then resolves its value using the breakpoint chain.
 *
 * @param {Array<Object>} mediaQueries - Array of media query objects from MediaQueriesProvider
 * @param {Object} mediaQueries[].query - The MediaQueryList object with .matches property
 * @param {string} mediaQueries[].breakpoint - The breakpoint name for this media query
 * @param {Object} breakpoints - The breakpoint values object to resolve values from
 * @returns {*|undefined} The resolved value for the active breakpoint, or undefined if no media query matches
 */
const findActiveBreakpointValue = (mediaQueries, breakpoints) => {
    const activeMqIndex = mediaQueries.findIndex((mq) => mq.query.matches)
    return findBreakpointValue(breakpoints, mediaQueries, activeMqIndex)
}

/**
 * Custom hook to get a value based on the current breakpoint. When a value is not defined for a breakpoint,
 * it will use the value from the previous breakpoint (if defined). On server side it will always return the first breakpoint value (`base`).
 *
 * Note: does not support SSR, or dynamic breakpoint values.
 * If you need a dynamic value, use a function as a value and execute it. Example:
 *
 * ```js
 * const someComputedValue = useBreakpoints({
 *   base: () => 'baseValue',
 *   md: () => 'smallValue',
 * })()
 * ```
 *
 * To memoize a complex computation then you need to memoize the function:
 * ```js
 * const calcBase = useCallback(() => 'base', [])
 * const calcMd = useCallback(() => 'small', [])
 * const calcFn = useBreakpoints({
 *   base: calcBase,
 *   md: calcMd,
 * })
 * const computedValue = useMemo(() => calcFn(), [calcFn])
 * ```
 *
 * @param {{
 *   base?: *,
 *   sm?: *,
 *   md?: *,
 *   lg?: *,
 *   xl?: *,
 *   '2xl'?: *
 * }} breakpointValues - an object with breakpoint names as keys and values as values.
 * @param {Object} [options] - options for the hook
 * @param {boolean} [options.isServer] - whether the code is running on the server or client side. Should not be used as it's defined for testing purposes only.
 * @returns {*}
 */
function useBreakpoints(breakpointValues) {
    const mediaQueries = useMediaQueries()

    const breakpointsRef = useRef(breakpointValues)
    // don't memoize but put in ref to avoid re-rendering
    // the initialBreakpoints is a new object every time, but values inside do not change
    breakpointsRef.current = breakpointValues

    const areQueriesDefined = mediaQueries?.[0]?.query !== undefined

    const [currentValue, setCurrentValue] = useState(() => {
        if (isServer || !areQueriesDefined) {
            // on server side or app without hydration we always return the first breakpoint value
            const firstMq = mediaQueries[0]
            return firstMq
                ? breakpointsRef.current[firstMq.breakpoint]
                : breakpointsRef.current[Object.keys(breakpointsRef.current)[0]] // return first breakpoint value on server or no hydration
        }

        // hydration was done and new media queries are available
        return findActiveBreakpointValue(mediaQueries, breakpointsRef.current)
    })

    useEffect(() => {
        if (isServer || !areQueriesDefined) return

        // hydration was done and new media queries are available
        setCurrentValue(findActiveBreakpointValue(mediaQueries, breakpointsRef.current))

        // Create abort controller for this effect instance it will make the handler removal cleaner
        const abortController = new AbortController()
        const {signal} = abortController

        // add event listeners to all media queries, mqlRef.current is guaranteed to be defined here
        mediaQueries.forEach((mq, index) => {
            const handleChange = (e) => {
                if (e.matches) {
                    const nextValue = findBreakpointValue(
                        breakpointsRef.current,
                        mediaQueries,
                        index
                    )
                    setCurrentValue(nextValue)
                }
            }

            mq.query.addEventListener('change', handleChange, {signal})
        })

        return () => {
            abortController.abort() // Cleanup all listeners
        }
    }, [mediaQueries, areQueriesDefined])

    return currentValue
}

export default useBreakpoints
